import React, { useState, useEffect, useMemo } from 'react';
import businessCategoryService from '../services/businessCategories';
import { BusinessCategory } from '../types';
import { Pagination } from '../components/Pagination';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, Loader2, Tag, Trash2, Edit, ToggleLeft, ToggleRight } from 'lucide-react';
import { useForm } from 'react-hook-form';

interface CategoryFormData {
  code: string;
  name: string;
  emoji?: string;
  description?: string;
}

export const BusinessCategoriesPage: React.FC = () => {
  const [allCategories, setAllCategories] = useState<BusinessCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState<BusinessCategory | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // Client-side pagination
  const totalElements = allCategories.length;
  const totalPages = Math.ceil(totalElements / pageSize);
  const categories = useMemo(() => {
    const start = currentPage * pageSize;
    return allCategories.slice(start, start + pageSize);
  }, [allCategories, currentPage, pageSize]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CategoryFormData>();

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await businessCategoryService.getAllCategoriesAdmin();
      setAllCategories(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (category?: BusinessCategory) => {
    if (category) {
      setEditingCategory(category);
      reset({
        code: category.code,
        name: category.name,
        emoji: category.emoji || '',
        description: category.description || '',
      });
    } else {
      setEditingCategory(null);
      reset({});
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCategory(null);
    reset({});
  };

  const onSubmit = async (data: CategoryFormData) => {
    try {
      if (editingCategory) {
        await businessCategoryService.updateCategory(editingCategory.id, data);
      } else {
        await businessCategoryService.createCategory(data);
      }
      await loadCategories();
      handleCloseDialog();
    } catch (err: any) {
      setError(err.message || 'Failed to save category');
    }
  };

  const handleToggleActive = async (id: number, isActive: boolean) => {
    try {
      if (isActive) {
        await businessCategoryService.deactivateCategory(id);
      } else {
        await businessCategoryService.activateCategory(id);
      }
      await loadCategories();
    } catch (err: any) {
      setError(err.message || 'Failed to toggle category status');
    }
  };

  const handleDelete = async (id: number) => {
    if (
      window.confirm('Are you sure you want to delete this category? This action cannot be undone.')
    ) {
      try {
        await businessCategoryService.deleteCategory(id);
        await loadCategories();
      } catch (err: any) {
        setError(err.message || 'Failed to delete category');
      }
    }
  };

  if (loading && categories.length === 0) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Business Categories</h1>
          <p className="text-muted-foreground">Manage business categories for branches</p>
        </div>
        <Button onClick={() => handleOpenDialog()}>
          <Plus className="h-4 w-4 mr-2" />
          Add Category
        </Button>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-destructive">
          <p className="font-medium">Error</p>
          <p className="text-sm">{error}</p>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>All Categories</CardTitle>
          <CardDescription>List of all business categories</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Emoji</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {categories.map((category) => (
                <TableRow key={category.id}>
                  <TableCell>
                    <span className="font-mono text-sm text-muted-foreground">{category.code}</span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Tag className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{category.name}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-2xl">{category.emoji || '-'}</span>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {category.description || '-'}
                  </TableCell>
                  <TableCell>
                    <Badge variant={category.active ? 'default' : 'secondary'}>
                      {category.active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleToggleActive(category.id, category.active)}
                      >
                        {category.active ? (
                          <ToggleRight className="h-4 w-4" />
                        ) : (
                          <ToggleLeft className="h-4 w-4" />
                        )}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(category)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(category.id)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {categories.length === 0 && (
            <p className="text-center text-sm text-muted-foreground py-8">No categories found</p>
          )}

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalElements={totalElements}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setCurrentPage(0);
            }}
            itemName="categories"
          />
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCategory ? 'Edit Category' : 'Create Category'}</DialogTitle>
            <DialogDescription>
              {editingCategory ? 'Update category details' : 'Add a new business category'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <Label htmlFor="code">Code *</Label>
              <Input
                id="code"
                {...register('code', { required: true })}
                placeholder="e.g., restaurant, hotel, spa"
                disabled={!!editingCategory}
              />
              {errors.code && <p className="text-sm text-destructive">Code is required</p>}
              {editingCategory && (
                <p className="text-xs text-muted-foreground">Code cannot be changed</p>
              )}
            </div>

            <div>
              <Label htmlFor="name">Category Name *</Label>
              <Input
                id="name"
                {...register('name', { required: true })}
                placeholder="e.g., Restaurant, Hotel, Spa"
              />
              {errors.name && <p className="text-sm text-destructive">Name is required</p>}
            </div>

            <div>
              <Label htmlFor="emoji">Emoji</Label>
              <Input
                id="emoji"
                {...register('emoji')}
                placeholder="e.g., 🍽️, 🏨, 💆"
                maxLength={2}
              />
              <p className="text-xs text-muted-foreground">Optional emoji for the category</p>
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                {...register('description')}
                placeholder="Optional description"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancel
              </Button>
              <Button type="submit">{editingCategory ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BusinessCategoriesPage;
